import { Capacitor } from "@capacitor/core";

type RevenueCatPlan = "monthly" | "yearly";

type RevenueCatPackage = {
  identifier?: string;
  product?: {
    identifier?: string;
    title?: string;
    description?: string;
    price?: number;
    priceString?: string;
  };
};

export type RevenueCatPlanOffer = {
  plan: RevenueCatPlan;
  package: unknown;
  price: number | null;
  priceLabel: string | null;
  title: string | null;
  description: string | null;
  productId: string | null;
};

export type RevenueCatOfferMap = Partial<Record<RevenueCatPlan, RevenueCatPlanOffer>>;

/** One consumable token pack from the "tokens" offering. */
export type RevenueCatTokenOffer = {
  tokens: number;
  package: unknown;
  price: number | null;
  priceLabel: string | null;
  productId: string | null;
};

export type RevenueCatPurchaseResult =
  | { status: "success" }
  | { status: "cancelled" }
  | { status: "error"; message: string };

const MONTHLY_HINTS = ["month", "monthly", "mth"];
const YEARLY_HINTS = ["year", "yearly", "annual", "yr"];

/** RevenueCat offering that lists the consumable token packs. */
const TOKENS_OFFERING_ID = "tokens";

// Token amount encoded in the product id: "tokens_100", "100TOKENS",
// "tokens-250"... Same rule as the backend webhook, which credits the amount
// it parses from the product id, so a pack the app cannot parse would not be
// credited either and is skipped.
const TOKEN_PRODUCT_PATTERN = /^(?:(\d+)[_-]?tokens?|tokens?[_-]?(\d+))$/i;

function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

function resolveProductId(pkg: RevenueCatPackage): string {
  return (
    pkg.product?.identifier ||
    pkg.identifier ||
    ""
  ).toLowerCase();
}

function parseTokenAmount(pkg: RevenueCatPackage): number | null {
  // Google Play ids may carry a ":base_plan" suffix.
  const productId = resolveProductId(pkg).split(":")[0].trim();
  const match = TOKEN_PRODUCT_PATTERN.exec(productId);
  if (!match) return null;
  const amount = Number.parseInt(match[1] ?? match[2] ?? "", 10);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function scorePlan(productId: string, hints: string[]): number {
  return hints.reduce((score, hint) => (productId.includes(hint) ? score + 1 : score), 0);
}

function inferPlan(pkg: RevenueCatPackage): RevenueCatPlan | null {
  const productId = resolveProductId(pkg);
  if (!productId) return null;
  const monthlyScore = scorePlan(productId, MONTHLY_HINTS);
  const yearlyScore = scorePlan(productId, YEARLY_HINTS);
  if (monthlyScore === 0 && yearlyScore === 0) return null;
  return monthlyScore >= yearlyScore ? "monthly" : "yearly";
}

async function getPurchasesModule() {
  if (!isNativePlatform()) {
    throw new Error("RevenueCat is only available on native platforms.");
  }
  return import("@revenuecat/purchases-capacitor");
}

export async function logInRevenueCatUser(appUserId: string): Promise<void> {
  const userId = appUserId?.trim();
  if (!userId) {
    throw new Error("Missing app user id for RevenueCat login.");
  }
  const { Purchases } = await getPurchasesModule();
  await Purchases.logIn({ appUserID: userId });
}

/**
 * Purchases must be made under the app user's UUID: the backend maps the
 * webhook's app_user_id straight to the user, an anonymous RevenueCat id
 * would grant premium to nobody. Called right before a purchase in case the
 * login after configure() failed or has not finished yet.
 */
export async function ensureRevenueCatUser(appUserId: string): Promise<void> {
  const userId = appUserId?.trim();
  if (!userId) {
    throw new Error("Missing app user id for RevenueCat purchase.");
  }
  const { Purchases } = await getPurchasesModule();
  const { appUserID } = await Purchases.getAppUserID();
  if (appUserID !== userId) {
    console.warn("[RevenueCat] SDK user", appUserID, "differs from app user, logging in");
    await Purchases.logIn({ appUserID: userId });
  }
}

export async function logOutRevenueCatUser(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { Purchases } = await getPurchasesModule();
    await Purchases.logOut();
  } catch (err) {
    // RC throws when already anonymous; not fatal.
    console.warn("[RevenueCat] logOut failed (likely already anonymous):", err);
  }
}

export async function getRevenueCatOffers(): Promise<RevenueCatOfferMap> {
  const { Purchases } = await getPurchasesModule();
  const offerings = await Purchases.getOfferings();
  const current = offerings.current;
  const availablePackages: RevenueCatPackage[] = (current?.availablePackages || []) as RevenueCatPackage[];
  const mapped: RevenueCatOfferMap = {};

  for (const pkg of availablePackages) {
    const plan = inferPlan(pkg);
    if (!plan || mapped[plan]) continue;
    mapped[plan] = {
      plan,
      package: pkg,
      price: typeof pkg.product?.price === "number" ? pkg.product.price : null,
      priceLabel: pkg.product?.priceString || null,
      title: pkg.product?.title || null,
      description: pkg.product?.description || null,
      productId: pkg.product?.identifier || pkg.identifier || null,
    };
  }

  return mapped;
}

/**
 * Token packs from the "tokens" offering, cheapest first. Only packs whose
 * product id encodes the token amount are returned (see TOKEN_PRODUCT_PATTERN).
 */
export async function getRevenueCatTokenOffers(): Promise<RevenueCatTokenOffer[]> {
  const { Purchases } = await getPurchasesModule();
  const offerings = await Purchases.getOfferings();
  const offering = offerings.all?.[TOKENS_OFFERING_ID];
  const availablePackages: RevenueCatPackage[] = (offering?.availablePackages || []) as RevenueCatPackage[];
  const offers: RevenueCatTokenOffer[] = [];

  for (const pkg of availablePackages) {
    const tokens = parseTokenAmount(pkg);
    if (!tokens || offers.some((offer) => offer.tokens === tokens)) continue;
    offers.push({
      tokens,
      package: pkg,
      price: typeof pkg.product?.price === "number" ? pkg.product.price : null,
      priceLabel: pkg.product?.priceString || null,
      productId: pkg.product?.identifier || pkg.identifier || null,
    });
  }

  return offers.sort((a, b) => a.tokens - b.tokens);
}

export async function purchaseRevenueCatPlan(planOffer: RevenueCatPlanOffer): Promise<RevenueCatPurchaseResult> {
  return purchaseRevenueCatPackage(planOffer.package);
}

export async function purchaseRevenueCatTokens(tokenOffer: RevenueCatTokenOffer): Promise<RevenueCatPurchaseResult> {
  return purchaseRevenueCatPackage(tokenOffer.package);
}

async function purchaseRevenueCatPackage(pkg: unknown): Promise<RevenueCatPurchaseResult> {
  try {
    const { Purchases } = await getPurchasesModule();
    await Purchases.purchasePackage({ aPackage: pkg as never });
    return { status: "success" };
  } catch (err: unknown) {
    const purchaseError = err as { code?: string | number; message?: string };
    const code = String(purchaseError.code || "").toUpperCase();
    const message = purchaseError.message || "RevenueCat purchase failed.";
    if (code.includes("USER_CANCELLED")) {
      return { status: "cancelled" };
    }
    return { status: "error", message };
  }
}

